import type { TankClient } from "@tactical-agent-neural-knowledge/sdk";
import type { Message } from "@tactical-agent-neural-knowledge/sdk/contracts/message";

export interface MessageSearch {
  /** Message id to scroll to and highlight (`?m=`). */
  m?: string;
  /** What to open on arrival: `goal`, the goal editor, for links that mean "set a goal here";
   * `code`, Tread settings at Code, for a hosting notice's "review it here". */
  open?: "goal" | "code";
}

/** Search-param validator for the Tread / DM routes. */
export function parseMessageSearch(search: Record<string, unknown>): MessageSearch {
  const out: MessageSearch = {};
  const m = search.m;
  if (typeof m === "string" && m !== "") out.m = m;
  if (search.open === "goal" || search.open === "code") out.open = search.open;
  return out;
}

/** `/w/$ws/c/$channel?m=$id` (or `/dm/`): the shareable path to a message. */
export function permalinkPath(wsKey: string, channelId: string, messageId: string, dm = false): string {
  return `/w/${encodeURIComponent(wsKey)}/${dm ? "dm" : "c"}/${encodeURIComponent(channelId)}?m=${encodeURIComponent(messageId)}`;
}

const PAGE = 100;
const MAX_PAGES = 20;
const WINDOW = 40;

function waitForFirstPage(client: TankClient, channelId: string, timeoutMs = 10_000): Promise<void> {
  const ready = () => {
    const p = client.store.getState().channelPaging[channelId];
    return !!p?.loaded && !p.loading;
  };
  if (ready()) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      off();
      resolve();
    }, timeoutMs);
    const off = client.store.subscribe(() => {
      if (!ready()) return;
      clearTimeout(timer);
      off();
      resolve();
    });
  });
}

/**
 * Make sure a message is in the store so the list can scroll to it. Fetches it with `ChatService.GetMessage`
 * when it is not loaded, then pages the Tread back until its seq is covered (up to `MAX_PAGES` × `PAGE`),
 * falling back to a window of messages around it. Thread replies just need the root: the thread pane loads them.
 */
export async function ensureMessageLoaded(
  client: TankClient,
  channelId: string,
  messageId: string,
): Promise<Message | undefined> {
  const store = client.store;
  const known = store.getState().messages[messageId];
  const fetched = known ?? (await client.chat.getMessage({ messageId })).message;
  if (!fetched) return undefined;
  const msg: Message = fetched;
  if (msg.channelId !== channelId) return msg;
  if (msg.threadRootId !== "" || msg.channelSeq === 0n) {
    store.dispatch({ type: "messages/upsert", messages: [msg] });
    return msg;
  }
  await waitForFirstPage(client, channelId);
  const seq = msg.channelSeq;
  const oldestLoaded = () => {
    const loaded = store.selectChannelMessages(channelId).filter((m) => m.channelSeq > 0n);
    return loaded[0]?.channelSeq ?? 0n;
  };
  let pages = 0;
  while (oldestLoaded() > seq && pages < MAX_PAGES) {
    pages++;
    const more = await client.loadChannel(channelId, { direction: "before", limit: PAGE });
    if (!more) break;
  }
  if (store.getState().messages[messageId]) return store.getState().messages[messageId];
  // Too far back for continuous paging: load a window around it. Older paging continues from the window;
  // the stretch between the window and the pages above stays unloaded until the user scrolls into it.
  const res = await client.chat.listMessages({
    channelId,
    beforeSeq: seq + 1n,
    afterSeq: 0n,
    limit: WINDOW,
    kinds: [],
  });
  const messages = res.messages.some((m) => m.id === msg.id) ? res.messages : [...res.messages, msg];
  store.dispatch({ type: "messages/upsert", messages });
  const min = messages.reduce((a, m) => (m.channelSeq > 0n && m.channelSeq < a ? m.channelSeq : a), seq);
  store.dispatch({ type: "paging/set", channelId, paging: { oldestSeq: min, hasMoreBefore: res.hasMore } });
  return msg;
}

/**
 * Make sure a message at or just after `seq` is loaded, and return its id.
 *
 * Scrubbing the Topo strip lands on a seq, not a message: the strip maps the
 * whole channel from seq while the list only holds what is loaded. If the seq
 * is within what is loaded, the nearest row at or after it will do. If not,
 * load a window ending there, the same way a permalink to old history does,
 * so the stretch above stays unloaded until somebody scrolls into it.
 */
export async function ensureSeqLoaded(
  client: TankClient,
  channelId: string,
  seq: bigint,
): Promise<string | undefined> {
  const store = client.store;
  const nearest = () => {
    const loaded = store.selectChannelMessages(channelId).filter((m) => m.channelSeq > 0n);
    return loaded.find((m) => m.channelSeq >= seq) ?? loaded[loaded.length - 1];
  };
  const loaded = store.selectChannelMessages(channelId).filter((m) => m.channelSeq > 0n);
  const oldest = loaded[0]?.channelSeq ?? 0n;
  const newest = loaded[loaded.length - 1]?.channelSeq ?? 0n;
  if (loaded.length > 0 && seq >= oldest && seq <= newest) return nearest()?.id;
  const res = await client.chat.listMessages({
    channelId,
    beforeSeq: seq + BigInt(WINDOW / 2),
    afterSeq: 0n,
    limit: WINDOW,
    kinds: [],
  });
  if (res.messages.length === 0) return nearest()?.id;
  store.dispatch({ type: "messages/upsert", messages: res.messages });
  const min = res.messages.reduce((a, m) => (m.channelSeq > 0n && m.channelSeq < a ? m.channelSeq : a), seq);
  const cur = store.getState().channelPaging[channelId];
  if (!cur?.oldestSeq || min < cur.oldestSeq) {
    store.dispatch({ type: "paging/set", channelId, paging: { oldestSeq: min, hasMoreBefore: res.hasMore } });
  }
  return nearest()?.id;
}
